/* ==========================================================================
   Video reels (#MY_INDIANFLOWER)
   The horizontal poster carousel is driven by the theme's <splide-carousel>.
   This element handles the full-screen vertical scroll-snap reels feed
   (Instagram Reels / YouTube Shorts style) with autoplay-on-scroll.
   ========================================================================== */

if (!customElements.get('video-reels')) {
  class VideoReels extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-reels-modal]');
      this.feed = this.querySelector('[data-reels-feed]');
      this.items = Array.from(this.querySelectorAll('[data-reels-item]'));
      this.muted = true;
      this.active = -1;

      // Open the feed from a card. Delegated so it survives Splide's cloned
      // slides and re-mounts.
      this.addEventListener('click', (event) => {
        const card = event.target.closest('[data-reels-open]');
        if (card && this.contains(card)) {
          event.preventDefault();
          this.open(parseInt(card.dataset.reelsOpen, 10) || 0);
        }
      });

      if (!this.modal) return;
      this.modal.classList.add('is-muted');

      this.querySelector('[data-reels-close]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-reels-mute]')?.addEventListener('click', () => this.toggleMute());
      this.querySelectorAll('[data-reels-tap]').forEach((tap) => {
        tap.addEventListener('click', () => this.togglePlay());
      });

      this._onKey = (e) => {
        if (this.modal.hasAttribute('hidden')) return;
        if (e.key === 'Escape') this.close();
      };
      document.addEventListener('keydown', this._onKey);

      // Autoplay the centred reel, pause the rest.
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              this.activate(this.items.indexOf(entry.target));
            }
          });
        },
        { root: this.feed, threshold: [0, 0.6, 1] }
      );
    }

    disconnectedCallback() {
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
      this.io?.disconnect();
    }

    /* ---- Feed open/close ---- */
    open(index) {
      this.modal.removeAttribute('hidden');
      document.body.classList.add('overflow-hidden');
      this.items.forEach((it) => this.io.observe(it));
      requestAnimationFrame(() => {
        this.feed.scrollTop = index * this.feed.clientHeight;
        this.activate(index);
      });
      this.querySelector('[data-reels-close]')?.focus();
    }

    close() {
      this.stopAll();
      this.items.forEach((it) => this.io.unobserve(it));
      this.active = -1;
      this.modal.setAttribute('hidden', '');
      document.body.classList.remove('overflow-hidden');
    }

    /* ---- Active reel handling ---- */
    activate(index) {
      if (index < 0 || index === this.active) return;
      this.active = index;
      this.items.forEach((item, i) => {
        if (i === index) this.playItem(item);
        else this.stopItem(item);
      });
    }

    playItem(item) {
      const video = item.querySelector('video');
      if (video) {
        video.muted = this.muted;
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      }
      const embed = item.querySelector('[data-embed-id]');
      if (embed && !embed.querySelector('iframe')) {
        const frame = this.buildIframe(embed.dataset.embedType, embed.dataset.embedId);
        if (frame) embed.insertAdjacentHTML('beforeend', frame);
      }
    }

    stopItem(item) {
      const video = item.querySelector('video');
      if (video) {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (e) {
          /* noop */
        }
      }
      const frame = item.querySelector('iframe');
      if (frame) frame.remove();
    }

    stopAll() {
      this.items.forEach((it) => this.stopItem(it));
    }

    togglePlay() {
      const item = this.items[this.active];
      const video = item?.querySelector('video');
      if (!video) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    }

    toggleMute() {
      this.muted = !this.muted;
      this.modal.classList.toggle('is-muted', this.muted);
      this.querySelectorAll('.reels-modal__video').forEach((v) => {
        if (v.tagName === 'VIDEO') v.muted = this.muted;
      });
      const item = this.items[this.active];
      const embed = item?.querySelector('[data-embed-id]');
      if (embed) {
        embed.querySelector('iframe')?.remove();
        const frame = this.buildIframe(embed.dataset.embedType, embed.dataset.embedId);
        if (frame) embed.insertAdjacentHTML('beforeend', frame);
      }
      this.querySelector('[data-reels-mute]')?.setAttribute('aria-label', this.muted ? 'Unmute' : 'Mute');
    }

    buildIframe(type, id) {
      const mute = this.muted ? 1 : 0;
      if (type === 'youtube') {
        return `<iframe src="https://www.youtube.com/embed/${id}?autoplay=1&mute=${mute}&loop=1&playlist=${id}&controls=1&playsinline=1&rel=0&modestbranding=1" title="Reel" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>`;
      }
      if (type === 'vimeo') {
        return `<iframe src="https://player.vimeo.com/video/${id}?autoplay=1&muted=${mute}&loop=1&title=0&byline=0&portrait=0" title="Reel" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
      }
      return '';
    }
  }

  customElements.define('video-reels', VideoReels);
}
